import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock } from 'lucide-react';

interface TimeoutModalProps {
  isOpen: boolean;
  countdownSeconds: number;
  onStayActive: () => void;
  onTimeout: () => void;
}

const TimeoutModal: React.FC<TimeoutModalProps> = ({
  isOpen,
  countdownSeconds,
  onStayActive,
  onTimeout,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(countdownSeconds);

  // Reset countdown when modal opens
  useEffect(() => {
    if (isOpen) {
      setSecondsLeft(countdownSeconds);
    }
  }, [isOpen, countdownSeconds]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen) return;

    const timer = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, onTimeout]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

          {/* Modal */}
          <motion.div
            className="relative bg-army-dark border-2 border-army-gold/50 rounded-lg p-8 max-w-md mx-4 text-center shadow-2xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-army-gold/10 border-2 border-army-gold/50 flex items-center justify-center">
                <Clock className="w-8 h-8 text-army-gold" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-white mb-3">
              ARE YOU STILL THERE?
            </h2>

            {/* Message */}
            <p className="text-gray-400 mb-6">
              This session will restart due to inactivity.
            </p>

            {/* Countdown */}
            <div className="mb-8">
              <div className="text-5xl font-bold text-army-gold font-hud">
                {secondsLeft}
              </div>
              <p className="text-sm text-gray-500 mt-1 font-hud">SECONDS REMAINING</p>
            </div>

            {/* Button */}
            <motion.button
              onClick={onStayActive}
              className="w-full px-8 py-4 bg-army-gold text-army-black font-bold text-lg rounded-lg hover:bg-army-gold/90 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              I'M STILL HERE
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TimeoutModal;
